import styles from './PromoCodeInput.module.scss';

import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import React, { useEffect, useState, FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  initialFetchingPromoValue,
  useFetchingPromoInternal,
} from '../FetchingPromoContext/useFetchingPromoInternal';
import { PROMO_CODE_INPUT_TEST_ID } from './PromoCodeInput.const';
import { useReseller } from 'hooks/Reseller/useReseller';
import { fetchPromotionDetails } from 'services/shop/cart';
import { useCartContext } from 'components/Cart/hooks/useCartContext';

interface IPromoCodeInputProps {
  handleProgramPromo?: () => void;
  isActivePromoApplicableToCartItems: boolean;
}

export const PromoCodeInput: FC<IPromoCodeInputProps> = ({
  handleProgramPromo,
  isActivePromoApplicableToCartItems,
}) => {
  const { t } = useTranslation();
  const { resellerId } = useReseller();
  const {
    cart: { products, cartMetadata },
    checkout: { promo: promoInCart },
    addPromo,
  } = useCartContext();
  const { setFetchingPromo } = useFetchingPromoInternal();

  const [error, setError] = useState<string>();
  const [promoCode, setPromoCode] = useState<string>('');

  const cartProductIds = useMemo(
    () => [...products.map(p => p.fdmpId), ...(cartMetadata?.hardware?.map(h => h.fdmpId) || [])],
    [products, cartMetadata],
  );

  const inputRef = React.createRef<HTMLInputElement>();

  useEffect(() => {
    setPromoCode(promoInCart?.applied ?? '');
  }, [promoInCart.applied]);

  useEffect(() => {
    if (promoInCart.applied || promoInCart.program) {
      if (!inputRef.current) return;

      inputRef.current.value = '';
      if (handleProgramPromo && promoInCart.program) handleProgramPromo();
    }
  }, [promoInCart.applied, promoInCart.program]);

  useEffect(() => {
    if (!resellerId || !promoCode || promoCode === '') return;

    if (!isActivePromoApplicableToCartItems && promoInCart.applied !== null && promoInCart.program !== null) {
      addPromo({
        program: null,
        applied: null,
      });
      return;
    }

    applyPromoCode();
  }, [promoCode, resellerId, cartProductIds]);

  const validateUserInput = () => {
    if (!inputRef.current || inputRef.current.value.trim() === '') {
      setError(t('promo-code.required'));
      setPromoCode('');
      return;
    }

    setPromoCode(inputRef.current.value.trim());
  };

  const applyPromoCode = async () => {
    const resellerIdOrBlank = resellerId ?? '';

    try {
      setFetchingPromo({ status: 'loading', data: null });

      const { data } = await fetchPromotionDetails({
        promoCode,
        resellerId: resellerIdOrBlank,
      });

      setError('');

      const promoApplicableProductIds = data.applicableToProducts.map(product => product.productId);
      const isPromoApplicableToCartProductIds = promoApplicableProductIds.some(productId =>
        cartProductIds.includes(productId),
      );

      if (!isPromoApplicableToCartProductIds) {
        setFetchingPromo(initialFetchingPromoValue);
        setError(t('promo-code.invalid-promo-for-your-cart-contents'));

        return;
      }

      setFetchingPromo({ status: 'success', data });
    } catch (e) {
      setFetchingPromo(initialFetchingPromoValue);

      if (!e.response) {
        setError(t('promo-code.api-error'));
      } else {
        setError(e.response.status === 404 ? t('promo-code.invalid-promo-code') : t('promo-code.api-error'));
      }
    }
  };

  return (
    <FormControl fullWidth>
      <TextField
        id="promoCode"
        name="promoCode"
        inputProps={{
          'data-testid': PROMO_CODE_INPUT_TEST_ID.input,
          maxLength: 24,
        }}
        label={t('promo-code.enter-code')}
        helperText={error}
        error={!!error}
        inputRef={inputRef}
        onKeyUp={e => e.key === 'Enter' && validateUserInput()}
      />
      <button
        className={styles.promoInputApplyButton}
        onClick={validateUserInput}
        data-testid={PROMO_CODE_INPUT_TEST_ID.apply}
      >
        {t('promo-code.apply')}
      </button>
    </FormControl>
  );
};

PromoCodeInput.displayName = 'PromoCodeInput';
